// ---------------------------------------------------------------------------
// Reference-extraction read-back — the deterministic half of S7 in
// docs/studio/wonbanking-studio-scenarios-2026-08-08.md.
//
// "Make it like this one" is not a conversion and not a clone. The developer
// is pointing at a standard-compliant reference screen, and what they want
// carried is the STANDARD — structure, terminology, patterns (HOW) — not the
// screen's own business content (WHAT). The scenarios doc is explicit that
// the required UI is "extract the standard from the reference", never
// "duplicate the reference", and that the HOW/WHAT separation must be
// visible on screen.
//
// What is decidable here: that the paste combines a reference DOCUMENT (a
// legacy screen source or a design document — the shapes Studio already
// reads) with a reference INTENT (처럼/참조/같은 … in the accompanying
// prose); the reference's structure; its visible terms (table columns,
// action labels, quoted labels); and that field-name identifiers are
// excluded by rule — an identifier belongs to the reference's contract, and
// the new screen's fields come from its own contract (S1) or field table
// (S2). What is NOT decidable is which visible term is standard vocabulary
// versus this screen's own: no deterministic rule can know that, and real
// corpus vocabulary must not ship into Studio (§0 leak discipline) — so that
// classification is the human's, and the default is the anti-clone bias:
// structure carries, terms do not until deliberately marked standard.
//
// A paste with conversion intent ("전환") or no prose at all stays in the
// S3 lane; prose without a document stays prose.
// ---------------------------------------------------------------------------

import {
  parseLegacyScreenReadback,
  type LegacyScreenReadback,
} from './legacy-screen-readback'
import {
  parseScreenDesignReadback,
  type ScreenDesignReadback,
} from './screen-design-readback'

export type ReferenceDocument =
  | { kind: 'legacy-screen'; readback: LegacyScreenReadback }
  | { kind: 'screen-design'; readback: ScreenDesignReadback }

export interface ReferenceExtractionReadback {
  source: 'reference-extraction'
  /** The developer's own words — what they want built LIKE the reference. */
  intent: string
  reference: ReferenceDocument
  /** Visible terms offered for the standard/specific sorting, first-seen order. */
  terms: string[]
  /** Field-name identifiers found in the reference — excluded by rule. */
  identifierCount: number
}

const REFERENCE_INTENT_PATTERN = /처럼|같이|같은|참조|본따|본떠|like this/i

/** A line that can only START a pasted document (markup or a heading). */
function isStructuralLine(line: string): boolean {
  return line.includes('<') || /^(?:#{1,4}\s+|\d{1,2}[.)]\s+|[■□▶●○◎]\s*\S)/.test(line)
}

/**
 * A line that belongs to a document's BODY once one has started — bullets
 * and flow lines are document content here, while the request the developer
 * typed above or below the paste is plain prose.
 */
function isDocumentContentLine(line: string): boolean {
  return isStructuralLine(line) || /^[-*]\s/.test(line) || /→|->/.test(line)
}

export function parseReferenceExtractionReadback(
  text: string,
): ReferenceExtractionReadback | null {
  const trimmed = text.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('```')) return null

  const lines = trimmed.split(/\r?\n/)
  let bodyStart = 0
  while (bodyStart < lines.length) {
    const line = lines[bodyStart].trim()
    if (line && isStructuralLine(line)) break
    bodyStart += 1
  }
  let bodyEnd = lines.length
  while (bodyEnd > bodyStart) {
    const line = lines[bodyEnd - 1].trim()
    if (line && isDocumentContentLine(line)) break
    bodyEnd -= 1
  }
  if (bodyStart >= bodyEnd) return null

  const intent = [...lines.slice(0, bodyStart), ...lines.slice(bodyEnd)]
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ')
  // No stated intent, or an intent that is not "like this" (전환해줘 is a
  // conversion and belongs to S3): not a reference extraction.
  if (intent.length < 4 || !REFERENCE_INTENT_PATTERN.test(intent)) return null

  const body = lines.slice(bodyStart, bodyEnd).join('\n')
  const legacy = parseLegacyScreenReadback(body)
  const reference: ReferenceDocument | null = legacy
    ? { kind: 'legacy-screen', readback: legacy }
    : (() => {
        const design = parseScreenDesignReadback(body)
        return design ? { kind: 'screen-design', readback: design } : null
      })()
  if (!reference) return null

  const terms: string[] = []
  const push = (term: string) => {
    const value = term.trim()
    if (value && !terms.includes(value)) terms.push(value)
  }
  let identifierCount = 0
  if (reference.kind === 'legacy-screen') {
    for (const table of reference.readback.tables) table.columns.forEach(push)
    for (const action of reference.readback.actions) push(action.label)
    identifierCount = reference.readback.fields.length
  } else {
    reference.readback.labels.forEach(push)
  }

  return {
    source: 'reference-extraction',
    intent,
    reference,
    terms,
    identifierCount,
  }
}

/**
 * Serializes the human's sorting. Stable keys; the exclusion list is written
 * out rather than implied, because "not carried" is the decision the
 * anti-clone default exists to make explicit — downstream must never treat
 * an excluded term as merely unseen.
 */
export function formatReferenceExtractionDecision(
  readback: ReferenceExtractionReadback,
  ruling: { structureCarried: boolean; termCarried: readonly boolean[] },
): string {
  const standard: string[] = []
  const excluded: string[] = []
  readback.terms.forEach((term, index) => {
    if (ruling.termCarried[index]) standard.push(term)
    else excluded.push(term)
  })

  const lines = ['[reference-extraction-decision]']
  lines.push(`structure: ${ruling.structureCarried ? 'carried' : 'not-carried'}`)
  if (standard.length > 0) lines.push(`standard-terms: ${standard.join(', ')}`)
  if (excluded.length > 0) {
    lines.push(`excluded-screen-specific: ${excluded.join(', ')}`)
  }
  return lines.join('\n')
}
